// components/Benchmark/BenchmarkPage.jsx — SCM Benchmark Pricing Database
import React, { useState, useMemo, useCallback } from "react";
import {
  Button, Field, Input, Select, Spinner, Text, Textarea, Tooltip,
} from "@fluentui/react-components";
import {
  Add24Regular, ArrowSync24Regular, Dismiss24Regular,
  DocumentArrowDown24Regular, Edit24Regular, Delete24Regular,
  Search24Regular, Eye24Regular, ArrowUpload24Regular,
  ArrowTrendingLines24Regular, DataBarVertical24Regular,
} from "@fluentui/react-icons";
import { useNavigate } from "react-router-dom";
import { useBenchmarkDB }   from "../../hooks/useBenchmarkDB";
import { useUserAccess }     from "../../context/UserAccessContext";
import { useMsal }           from "@azure/msal-react";
import { generateBenchmarkExcel } from "../../services/reportService";
import {
  BENCHMARK_CATEGORIES,
  CATEGORY_PHASE,
  PHASE_LABELS,
} from "../../services/benchmarkService";
import {
  assessRate, shouldCost, fmtMoney, fmtSignedPct,
} from "../../services/priceIntelligenceService";
import { usePagination, Pagination, useDebounced } from "../common/Pagination";

// ── Helpers ────────────────────────────────────────────────────────────────────

const PHASE_STYLE = {
  1: { bg: "#EBF3FF", color: "#005A9E", label: "Phase 1" },
  2: { bg: "#EAF6EA", color: "#006840", label: "Phase 2" },
  3: { bg: "#FFF4EC", color: "#7B5200", label: "Phase 3" },
};

const CATEGORY_COLORS = [
  "#0066FF","#006E6E","#7B2D8B","#C4262E","#D1521A","#637074",
  "#005A9E","#006840","#B87000","#8B3A3A","#2D6A8B","#4A7B4A",
  "#6B4A8B","#8B6A2D",
];

function catColor(cat) {
  const idx = BENCHMARK_CATEGORIES.indexOf(cat);
  return CATEGORY_COLORS[idx >= 0 ? idx : 0];
}

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function fmtNum(n, decimals = 2) {
  if (!n && n !== 0) return "—";
  return Number(n).toLocaleString("en-NG", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

function calcPct(awarded, benchmark) {
  if (!benchmark || benchmark === 0) return 0;
  return ((awarded - benchmark) / benchmark) * 100;
}

function PctBadge({ value }) {
  if (!value && value !== 0) return <span style={{ color: "#AAA" }}>—</span>;
  const pct = Number(value);
  const color  = pct < 0 ? "#107C10" : pct > 0 ? "#C4262E" : "#605E5C";
  const bg     = pct < 0 ? "#EAF6EA" : pct > 0 ? "#FDE7E9" : "#F3F2F1";
  return (
    <span style={{ background: bg, color, padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}>
      {pct > 0 ? "+" : ""}{pct.toFixed(1)}%
    </span>
  );
}

// ── Live market intelligence inside the form ──────────────────────────────────
// Fires as the buyer fills in category, commodity and rate — so the market
// position is known before the award is committed, not after.

const TONE_COLOR = { good: "#107C10", neutral: "#0052A3", warn: "#C4762A", bad: "#C4262E" };

function LiveRateIntelligence({ records, form, excludeId }) {
  const pool = useMemo(
    () => (records || []).filter((r) => r.id !== excludeId),
    [records, excludeId],
  );

  const query = useMemo(() => ({
    category: form.category,
    commodityGroup: form.commodityGroup,
    unitOfMeasure: form.unitOfMeasure,
    currency: form.currency || "NGN",
    location: form.location,
    targetDate: form.tenderDate || new Date().toISOString().slice(0, 10),
  }), [form.category, form.commodityGroup, form.unitOfMeasure, form.currency, form.location, form.tenderDate]);

  // The rate is scored as it is typed, so settle briefly before recomputing.
  const awarded = useDebounced(parseFloat(form.awardedRate) || 0, 300);

  const result = useMemo(() => {
    if (!form.category) return null;
    return awarded > 0
      ? assessRate(pool, query, awarded)
      : { available: false, shouldCost: shouldCost(pool, query) };
  }, [pool, query, awarded, form.category]);

  if (!result) return null;
  const sc = result.shouldCost;

  if (!sc.available) {
    return (
      <div style={{ gridColumn: "1 / -1", background: "#F0F4FA", border: "1px solid #D6E2F0", borderRadius: 9, padding: "11px 14px" }}>
        <Text style={{ fontSize: 11.5, color: "#44536A", lineHeight: 1.6 }}>
          <strong>Market check:</strong> {sc.reason || "no comparable award history yet."}
        </Text>
      </div>
    );
  }

  const tone = result.available ? TONE_COLOR[result.tone] : "#0052A3";

  return (
    <div style={{ gridColumn: "1 / -1", background: `${tone}0A`, border: `1.5px solid ${tone}33`, borderRadius: 10, padding: "13px 16px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
        <ArrowTrendingLines24Regular style={{ color: tone, fontSize: 16 }} />
        <Text style={{ fontSize: 11, fontWeight: 800, color: tone, letterSpacing: "0.5px" }}>MARKET CHECK</Text>
        {result.available && (
          <span style={{ background: `${tone}1A`, color: tone, padding: "2px 10px", borderRadius: 10, fontSize: 10.5, fontWeight: 800 }}>
            {result.verdict.toUpperCase()}
          </span>
        )}
        <span style={{ fontSize: 10.5, color: "#888", marginLeft: "auto" }}>
          {sc.n} comparable award{sc.n === 1 ? "" : "s"} · {sc.matchLevel} · {sc.confidence.level.toLowerCase()} confidence
        </span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: result.available ? 10 : 0 }}>
        {[
          { label: "STRETCH", value: sc.estimate.low, c: "#107C10" },
          { label: "TARGET", value: sc.estimate.target, c: "#A66400" },
          { label: "WALK-AWAY", value: sc.estimate.high, c: "#C4762A" },
        ].map((b) => (
          <div key={b.label} style={{ background: "#fff", borderRadius: 7, padding: "8px 10px", border: "1px solid #EAEDF2" }}>
            <Text style={{ fontSize: 9, fontWeight: 700, color: "#999", letterSpacing: "0.4px", display: "block" }}>{b.label}</Text>
            <Text style={{ fontSize: 12.5, fontWeight: 800, color: b.c, fontVariantNumeric: "tabular-nums" }}>
              {fmtMoney(b.value, sc.currency, 0)}
            </Text>
          </div>
        ))}
      </div>

      {result.available ? (
        <>
          <Text style={{ fontSize: 12, color: "#444", lineHeight: 1.65, display: "block" }}>{result.headline}</Text>
          {result.percentileRank !== null && (
            <Text style={{ fontSize: 11, color: "#777", display: "block", marginTop: 3 }}>
              {result.percentileRank.toFixed(0)}th percentile of comparable awards
              {sc.escalation.rate ? ` · category running at ${fmtSignedPct(sc.escalation.rate)} a year` : ""}
            </Text>
          )}
          {result.negotiation.levers.length > 0 && (
            <div style={{ marginTop: 9, paddingTop: 9, borderTop: `1px solid ${tone}22`, display: "flex", flexDirection: "column", gap: 5 }}>
              {result.negotiation.levers.slice(0, 2).map((l, i) => (
                <Text key={i} style={{ fontSize: 11.5, color: "#5A4200", lineHeight: 1.6 }}>💡 {l}</Text>
              ))}
            </div>
          )}
        </>
      ) : (
        <Text style={{ fontSize: 11.5, color: "#555", lineHeight: 1.6 }}>
          Enter the awarded rate to score it against these historical awards.
        </Text>
      )}
    </div>
  );
}

// ── Close-Out Form (create / edit) ────────────────────────────────────────────

const EMPTY_FORM = {
  tenderRef: "", category: "", location: "", commodityGroup: "",
  serviceDescription: "", supplierName: "", unitOfMeasure: "",
  benchmarkRate: "", lowestRate: "", awardedRate: "", currency: "NGN",
  commercialTerms: "", awardJustification: "",
  tenderDate: "", contractDuration: "",
  costSavingAchieved: "", marketObservation: "",
  phase: 3,
};

function CloseOutForm({ initial, onSave, onClose, submitting, records }) {
  const isEdit = !!initial;
  const [f, setF] = useState(() => {
    if (!initial) return { ...EMPTY_FORM };
    return {
      tenderRef:          initial.tenderRef || "",
      category:           initial.category  || "",
      location:           initial.location  || "",
      commodityGroup:     initial.commodityGroup || "",
      serviceDescription: initial.serviceDescription || "",
      supplierName:       initial.supplierName || "",
      unitOfMeasure:      initial.unitOfMeasure || "",
      benchmarkRate:      initial.benchmarkRate || "",
      lowestRate:         initial.lowestRate || "",
      awardedRate:        initial.awardedRate || "",
      currency:           initial.currency || "NGN",
      commercialTerms:    initial.commercialTerms || "",
      awardJustification: initial.awardJustification || "",
      tenderDate:         initial.tenderDate || "",
      contractDuration:   initial.contractDuration || "",
      costSavingAchieved: initial.costSavingAchieved || "",
      marketObservation:  initial.marketObservation || "",
      phase:              initial.phase || 3,
    };
  });

  const set = (k, v) => setF((p) => {
    const next = { ...p, [k]: v };
    if (k === "category") next.phase = CATEGORY_PHASE[v] || 3;
    if (k === "benchmarkRate" || k === "awardedRate") {
      const bm = parseFloat(k === "benchmarkRate" ? v : next.benchmarkRate) || 0;
      const aw = parseFloat(k === "awardedRate"   ? v : next.awardedRate)   || 0;
      next._pctPreview = bm > 0 ? calcPct(aw, bm) : null;
    }
    return next;
  });

  const pct = useMemo(() => {
    const bm = parseFloat(f.benchmarkRate) || 0;
    const aw = parseFloat(f.awardedRate)   || 0;
    return bm > 0 ? calcPct(aw, bm) : null;
  }, [f.benchmarkRate, f.awardedRate]);

  function handleSubmit() {
    const bm = parseFloat(f.benchmarkRate) || 0;
    const aw = parseFloat(f.awardedRate)   || 0;
    onSave({
      ...f,
      benchmarkRate:      bm,
      lowestRate:         parseFloat(f.lowestRate) || 0,
      awardedRate:        aw,
      percentageChange:   bm > 0 ? calcPct(aw, bm) : 0,
      costSavingAchieved: parseFloat(f.costSavingAchieved) || 0,
      phase:              CATEGORY_PHASE[f.category] || f.phase || 3,
    });
  }

  const canSubmit = f.tenderRef.trim() && f.category && f.supplierName.trim() &&
    f.awardedRate && f.currency && f.tenderDate && f.serviceDescription.trim();

  const inp = { style: { width: "100%", boxSizing: "border-box" } };

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", zIndex: 290, backdropFilter: "blur(2px)" }} />
      <div style={{
        position: "fixed", top: 0, right: 0, width: 620, height: "100vh",
        background: "#fff", zIndex: 300, boxShadow: "-4px 0 32px rgba(0,0,0,0.14)",
        display: "flex", flexDirection: "column",
      }}>
        {/* Header */}
        <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid #EDEBE9", background: "linear-gradient(135deg,#A66400 0%,#C87800 100%)", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div>
              <Text style={{ color: "#fff", fontWeight: 800, fontSize: 16, display: "block" }}>
                {isEdit ? "Edit Benchmark Record" : "Tender Close-Out Form"}
              </Text>
              <Text style={{ color: "rgba(255,255,255,0.7)", fontSize: 12 }}>
                {isEdit ? "Update pricing record" : "Submit at close-out of every tendering exercise"}
              </Text>
            </div>
            <Button appearance="subtle" icon={<Dismiss24Regular />} onClick={onClose} style={{ color: "#fff" }} />
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>

          {/* Section 1: Core Info */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", color: "#B87000", textTransform: "uppercase", marginBottom: 12, paddingBottom: 6, borderBottom: "1px solid #FFE8B0" }}>
              Core Tender Information
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div style={{ gridColumn: "1 / -1" }}>
                <Field label="Tender Reference *" required>
                  <Input value={f.tenderRef} onChange={(_, d) => set("tenderRef", d.value)} placeholder="e.g. LKL/SCM/LOG/2026/001" {...inp} />
                </Field>
              </div>
              <Field label="Category *" required>
                <Select value={f.category} onChange={(_, d) => set("category", d.value)} {...inp}>
                  <option value="">— Select Category —</option>
                  {BENCHMARK_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </Select>
              </Field>
              <Field label="Phase (auto-set from category)">
                <div style={{ padding: "6px 10px", background: "#F5F5F5", borderRadius: 6, border: "1px solid #E0E0E0", fontSize: 13 }}>
                  {f.category ? (PHASE_LABELS[CATEGORY_PHASE[f.category] || 3] || "—") : "Set by category"}
                </div>
              </Field>
              <Field label="Location">
                <Input value={f.location} onChange={(_, d) => set("location", d.value)} placeholder="e.g. Lagos, Offshore, Port Harcourt" {...inp} />
              </Field>
              <Field label="Commodity Group">
                <Input value={f.commodityGroup} onChange={(_, d) => set("commodityGroup", d.value)} placeholder="e.g. Marine Vessels, Drilling Services" {...inp} />
              </Field>
            </div>
          </div>

          {/* Section 2: Supplier & Rates */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", color: "#B87000", textTransform: "uppercase", marginBottom: 12, paddingBottom: 6, borderBottom: "1px solid #FFE8B0" }}>
              Supplier &amp; Pricing
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="Supplier Name *" required>
                <Input value={f.supplierName} onChange={(_, d) => set("supplierName", d.value)} placeholder="Awarded supplier" {...inp} />
              </Field>
              <Field label="Unit of Measure">
                <Input value={f.unitOfMeasure} onChange={(_, d) => set("unitOfMeasure", d.value)} placeholder="e.g. Per Trip, Per MT, Lumpsum" {...inp} />
              </Field>
              <Field label="Currency *" required>
                <Select value={f.currency} onChange={(_, d) => set("currency", d.value)} {...inp}>
                  <option value="NGN">NGN — Nigerian Naira</option>
                  <option value="USD">USD — US Dollar</option>
                  <option value="EUR">EUR — Euro</option>
                </Select>
              </Field>
              <Field label="Benchmark / Previous Rate">
                <Input type="number" value={String(f.benchmarkRate)} onChange={(_, d) => set("benchmarkRate", d.value)} placeholder="0.00" {...inp} />
              </Field>
              <Field label="Lowest Rate Received">
                <Input type="number" value={String(f.lowestRate)} onChange={(_, d) => set("lowestRate", d.value)} placeholder="0.00" {...inp} />
              </Field>
              <Field label="Awarded Rate *" required>
                <Input type="number" value={String(f.awardedRate)} onChange={(_, d) => set("awardedRate", d.value)} placeholder="0.00" {...inp} />
              </Field>
              {pct !== null && (
                <div style={{ gridColumn: "1 / -1", background: pct < 0 ? "#EAF6EA" : pct > 0 ? "#FDE7E9" : "#F3F2F1", borderRadius: 8, padding: "10px 14px", display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 12, color: "#666" }}>% Change vs Benchmark:</span>
                  <PctBadge value={pct} />
                  <span style={{ fontSize: 11, color: "#999" }}>{pct < 0 ? "Rate decreased — cost saving" : pct > 0 ? "Rate increased vs benchmark" : "No change"}</span>
                </div>
              )}
              <Field label="Cost Saving Achieved">
                <Input type="number" value={String(f.costSavingAchieved)} onChange={(_, d) => set("costSavingAchieved", d.value)} placeholder="0.00" {...inp} />
              </Field>
              <LiveRateIntelligence records={records} form={f} excludeId={initial?.id} />
            </div>
          </div>

          {/* Section 3: Contract Details */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", color: "#B87000", textTransform: "uppercase", marginBottom: 12, paddingBottom: 6, borderBottom: "1px solid #FFE8B0" }}>
              Contract Details
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="Tender Date *" required>
                <Input type="date" value={f.tenderDate} onChange={(_, d) => set("tenderDate", d.value)} {...inp} />
              </Field>
              <Field label="Contract Duration">
                <Input value={f.contractDuration} onChange={(_, d) => set("contractDuration", d.value)} placeholder="e.g. 12 months, 2 years, One-off" {...inp} />
              </Field>
            </div>
          </div>

          {/* Section 4: Narrative */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", color: "#B87000", textTransform: "uppercase", marginBottom: 12, paddingBottom: 6, borderBottom: "1px solid #FFE8B0" }}>
              Narrative &amp; Intelligence
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <Field label="Service Description *" required>
                <Textarea value={f.serviceDescription} onChange={(_, d) => set("serviceDescription", d.value)} placeholder="Full description of the product or service procured..." rows={3} style={{ width: "100%", boxSizing: "border-box" }} />
              </Field>
              <Field label="Commercial Terms">
                <Textarea value={f.commercialTerms} onChange={(_, d) => set("commercialTerms", d.value)} placeholder="Payment terms, Incoterms, mobilization conditions..." rows={2} style={{ width: "100%", boxSizing: "border-box" }} />
              </Field>
              <Field label="Award Justification">
                <Textarea value={f.awardJustification} onChange={(_, d) => set("awardJustification", d.value)} placeholder="Why this supplier was selected over others..." rows={2} style={{ width: "100%", boxSizing: "border-box" }} />
              </Field>
              <Field label="Market Observation">
                <Textarea value={f.marketObservation} onChange={(_, d) => set("marketObservation", d.value)} placeholder="Market intelligence, supplier commentary, price drivers..." rows={2} style={{ width: "100%", boxSizing: "border-box" }} />
              </Field>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: "14px 24px", borderTop: "1px solid #EDEBE9", display: "flex", gap: 8, background: "#FAFBFF", flexShrink: 0 }}>
          <Button
            appearance="primary"
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
            style={{ background: "#C87800" }}
          >
            {submitting ? <Spinner size="tiny" /> : isEdit ? "Save Changes" : "Submit Close-Out"}
          </Button>
          <Button appearance="secondary" onClick={onClose} disabled={submitting}>Cancel</Button>
        </div>
      </div>
    </>
  );
}

// ── Detail panel ────────────────────────────────────────────────────────────────

function DetailPanel({ record, onClose, onEdit, onDelete, canEdit, isAdmin }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const ps = PHASE_STYLE[record.phase] || PHASE_STYLE[3];
  const cc = catColor(record.category);
  const pct = record.percentageChange;

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.20)", zIndex: 290, backdropFilter: "blur(2px)" }} />
      <div style={{
        position: "fixed", top: 0, right: 0, width: 540, height: "100vh",
        background: "#fff", zIndex: 300, boxShadow: "-4px 0 32px rgba(0,0,0,0.14)",
        display: "flex", flexDirection: "column",
      }}>
        {/* Header */}
        <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid #EDEBE9", background: "#FAFBFF", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
            <div style={{ width: 6, minHeight: 60, borderRadius: 3, background: cc, flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
                <span style={{ background: `${cc}18`, color: cc, padding: "3px 10px", borderRadius: 20, fontSize: 10, fontWeight: 700 }}>{record.category}</span>
                <span style={{ background: ps.bg, color: ps.color, padding: "3px 10px", borderRadius: 20, fontSize: 10, fontWeight: 700 }}>{ps.label}</span>
                <span style={{ background: "#F3F2F1", color: "#605E5C", padding: "3px 10px", borderRadius: 20, fontSize: 10, fontWeight: 700 }}>{record.currency}</span>
              </div>
              <Text size={500} weight="bold" style={{ color: "#001A3D", lineHeight: 1.3, display: "block" }}>{record.tenderRef}</Text>
              <Text style={{ fontSize: 12, color: "#666", display: "block", marginTop: 3 }}>{record.supplierName}</Text>
            </div>
            <Button appearance="subtle" icon={<Dismiss24Regular />} onClick={onClose} style={{ flexShrink: 0 }} />
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, padding: "20px 24px", overflowY: "auto" }}>

          {/* Rate summary */}
          <div style={{ background: "linear-gradient(135deg,#FFF3DC,#FFF9EE)", border: "1px solid #FFE08A", borderRadius: 12, padding: "16px", marginBottom: 16 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: pct ? 10 : 0 }}>
              <div>
                <Text style={{ fontSize: 10, fontWeight: 700, color: "#8A6000", letterSpacing: "0.4px", display: "block", marginBottom: 4 }}>BENCHMARK RATE</Text>
                <Text style={{ fontSize: 14, fontWeight: 700, color: "#001A3D" }}>{record.benchmarkRate ? fmtNum(record.benchmarkRate) : "—"}</Text>
              </div>
              <div>
                <Text style={{ fontSize: 10, fontWeight: 700, color: "#8A6000", letterSpacing: "0.4px", display: "block", marginBottom: 4 }}>LOWEST RATE</Text>
                <Text style={{ fontSize: 14, fontWeight: 700, color: "#001A3D" }}>{record.lowestRate ? fmtNum(record.lowestRate) : "—"}</Text>
              </div>
              <div>
                <Text style={{ fontSize: 10, fontWeight: 700, color: "#8A6000", letterSpacing: "0.4px", display: "block", marginBottom: 4 }}>AWARDED RATE</Text>
                <Text style={{ fontSize: 14, fontWeight: 800, color: "#A66400" }}>{fmtNum(record.awardedRate)}</Text>
              </div>
            </div>
            {pct !== 0 && (
              <div style={{ paddingTop: 10, borderTop: "1px solid #FFD060", display: "flex", alignItems: "center", gap: 10 }}>
                <Text style={{ fontSize: 11, color: "#8A6000" }}>% Change vs Benchmark:</Text>
                <PctBadge value={pct} />
                {record.costSavingAchieved > 0 && (
                  <Text style={{ fontSize: 11, color: "#107C10", marginLeft: "auto" }}>
                    Saving: {record.currency} {fmtNum(record.costSavingAchieved)}
                  </Text>
                )}
              </div>
            )}
          </div>

          {/* Meta grid */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
            {[
              { label: "TENDER DATE", value: fmtDate(record.tenderDate) },
              { label: "CONTRACT DURATION", value: record.contractDuration || "—" },
              { label: "LOCATION", value: record.location || "—" },
              { label: "COMMODITY GROUP", value: record.commodityGroup || "—" },
              { label: "UNIT OF MEASURE", value: record.unitOfMeasure || "—" },
              { label: "SUBMITTED BY", value: record.submittedByName || record.submittedBy || "—" },
            ].map(({ label, value }) => (
              <div key={label} style={{ background: "#F8F9FC", borderRadius: 8, padding: "10px 12px" }}>
                <Text style={{ fontSize: 10, fontWeight: 700, color: "#888", letterSpacing: "0.4px", display: "block", marginBottom: 3 }}>{label}</Text>
                <Text style={{ fontSize: 12, color: "#001A3D", fontWeight: 600 }}>{value}</Text>
              </div>
            ))}
          </div>

          {/* Narrative fields */}
          {[
            { label: "SERVICE DESCRIPTION", value: record.serviceDescription },
            { label: "COMMERCIAL TERMS", value: record.commercialTerms },
            { label: "AWARD JUSTIFICATION", value: record.awardJustification },
            { label: "MARKET OBSERVATION", value: record.marketObservation },
          ].map(({ label, value }) => value ? (
            <div key={label} style={{ background: "#F5F8FF", border: "1px solid #D6E8FF", borderRadius: 10, padding: "12px 14px", marginBottom: 10 }}>
              <Text style={{ fontSize: 10, fontWeight: 700, color: "#0066FF", letterSpacing: "0.4px", display: "block", marginBottom: 6 }}>{label}</Text>
              <Text style={{ fontSize: 13, color: "#333", lineHeight: 1.7 }}>{value}</Text>
            </div>
          ) : null)}
        </div>

        {/* Footer */}
        <div style={{ padding: "14px 24px", borderTop: "1px solid #EDEBE9", display: "flex", gap: 8, background: "#FAFBFF", flexShrink: 0, alignItems: "center" }}>
          {canEdit && (
            <Button appearance="subtle" icon={<Edit24Regular />} onClick={() => onEdit(record)} style={{ color: "#C87800" }}>
              Edit
            </Button>
          )}
          {isAdmin && !confirmDelete && (
            <Button appearance="subtle" icon={<Delete24Regular />} onClick={() => setConfirmDelete(true)} style={{ color: "#D13438" }}>
              Delete
            </Button>
          )}
          {confirmDelete && (
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <Text style={{ fontSize: 12, color: "#D13438" }}>Delete this record?</Text>
              <Button appearance="primary" size="small" onClick={() => { onDelete(record.id); onClose(); }} style={{ background: "#D13438" }}>Yes, Delete</Button>
              <Button appearance="secondary" size="small" onClick={() => setConfirmDelete(false)}>Cancel</Button>
            </div>
          )}
          <Button appearance="secondary" onClick={onClose} style={{ marginLeft: "auto" }}>Close</Button>
        </div>
      </div>
    </>
  );
}

// ── Stat card ───────────────────────────────────────────────────────────────────

function StatCard({ label, value, sub, color, bg }) {
  return (
    <div style={{ flex: 1, minWidth: 130, background: "#fff", border: `1.5px solid ${color}28`, borderRadius: 14, padding: "16px 18px", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: `${color}18`, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <DataBarVertical24Regular style={{ color, fontSize: 18 }} />
        </div>
        <span style={{ fontSize: 26, fontWeight: 800, color: "#001A3D", lineHeight: 1 }}>{value}</span>
      </div>
      <span style={{ fontSize: 10, fontWeight: 700, color: "#888", letterSpacing: "0.6px", display: "block" }}>{label.toUpperCase()}</span>
      {sub && <span style={{ fontSize: 11, color }}>{sub}</span>}
    </div>
  );
}

// ── Main page ───────────────────────────────────────────────────────────────────

export default function BenchmarkPage() {
  const { records, loading, isDemoMode, spError, refresh, addRecord, editRecord, removeRecord } = useBenchmarkDB();
  const { canEdit, isAdmin, isStaff } = useUserAccess();
  const { accounts } = useMsal();
  const navigate = useNavigate();

  const currentUser     = accounts[0];
  const currentEmail    = currentUser?.username || "";
  const currentUserName = currentUser?.name     || "SCM User";

  const [selected,    setSelected]    = useState(null);
  const [formOpen,    setFormOpen]    = useState(false);
  const [editTarget,  setEditTarget]  = useState(null);
  const [submitting,  setSubmitting]  = useState(false);
  const [filterCat,   setFilterCat]   = useState("All");
  const [filterPhase, setFilterPhase] = useState("All");
  const [filterYear,  setFilterYear]  = useState("All");
  const [search,      setSearch]      = useState("");
  const [exporting,   setExporting]   = useState(false);
  const [reportDate,  setReportDate]  = useState(new Date().toISOString().slice(0, 10));

  const canSubmit = canEdit || isStaff;

  const years = useMemo(() => {
    const ys = new Set(records.map(r => r.tenderDate?.slice(0, 4)).filter(Boolean));
    return [...ys].sort().reverse();
  }, [records]);

  const debouncedSearch = useDebounced(search, 220);

  const filtered = useMemo(() => {
    return records.filter(r => {
      if (filterCat   !== "All" && r.category  !== filterCat)   return false;
      if (filterPhase !== "All" && String(r.phase) !== filterPhase) return false;
      if (filterYear  !== "All" && r.tenderDate?.slice(0, 4) !== filterYear) return false;
      if (debouncedSearch.trim()) {
        const q = debouncedSearch.toLowerCase();
        return r.tenderRef?.toLowerCase().includes(q) ||
          r.serviceDescription?.toLowerCase().includes(q) ||
          r.commodityGroup?.toLowerCase().includes(q) ||
          r.unitOfMeasure?.toLowerCase().includes(q) ||
          r.supplierName?.toLowerCase().includes(q) ||
          r.category?.toLowerCase().includes(q) ||
          r.location?.toLowerCase().includes(q);
      }
      return true;
    });
  }, [records, filterCat, filterPhase, filterYear, debouncedSearch]);

  const pager = usePagination(filtered, {
    defaultPageSize: 50,
    resetKey: `${filterCat}|${filterPhase}|${filterYear}|${debouncedSearch}`,
  });

  const stats = useMemo(() => {
    const savings = records.reduce((s, r) => s + (r.costSavingAchieved || 0), 0);
    const withPct = records.filter(r => r.benchmarkRate > 0);
    const avgPct  = withPct.length > 0
      ? withPct.reduce((s, r) => s + (r.percentageChange || 0), 0) / withPct.length
      : null;
    return {
      total:      records.length,
      savings,
      categories: new Set(records.map(r => r.category)).size,
      avgPct,
    };
  }, [records]);

  const openEdit = useCallback((rec) => {
    setSelected(null);
    setEditTarget(rec);
    setFormOpen(true);
  }, []);

  async function handleSave(data) {
    setSubmitting(true);
    try {
      if (editTarget) {
        const updated = await editRecord(editTarget.id, { ...data, submittedBy: editTarget.submittedBy, submittedByName: editTarget.submittedByName });
        if (selected?.id === editTarget.id) setSelected(updated);
      } else {
        await addRecord({ ...data, submittedBy: currentEmail, submittedByName: currentUserName });
      }
      setFormOpen(false);
      setEditTarget(null);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(id) {
    try { await removeRecord(id); } catch (e) { alert("Delete failed: " + e.message); }
  }

  function handleExport() {
    setExporting(true);
    try { generateBenchmarkExcel(filtered, reportDate); }
    finally { setTimeout(() => setExporting(false), 600); }
  }

  function openForm() { setEditTarget(null); setFormOpen(true); }
  function closeForm() { setFormOpen(false); setEditTarget(null); }

  return (
    <div style={{ maxWidth: 1320, margin: "0 auto", padding: "0 2px" }}>

      {/* ── HEADER ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, gap: 12, flexWrap: "wrap" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: "linear-gradient(135deg,#A66400,#C87800)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <DataBarVertical24Regular style={{ color: "#fff", fontSize: 20 }} />
            </div>
            <div>
              <Text size={600} weight="bold" style={{ color: "#001A3D", lineHeight: 1.2, display: "block" }}>Benchmark Pricing Database</Text>
              <Text style={{ fontSize: 12, color: "#888" }}>LekOil SCM — centralized tender and pricing intelligence</Text>
            </div>
          </div>
          {isDemoMode && <span style={{ fontSize: 11, fontWeight: 700, color: "#E65C00", background: "#FFF4EC", border: "1px solid #FFD6A8", padding: "2px 10px", borderRadius: 20 }}>Demo Mode</span>}
          {spError && <span style={{ fontSize: 11, color: "#D13438", marginLeft: 8 }}>SharePoint: {spError}</span>}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <Button
            appearance="subtle"
            icon={<ArrowTrendingLines24Regular />}
            onClick={() => navigate("/benchmark/intelligence")}
            style={{ color: "#A66400", fontWeight: 700 }}
          >
            Market Intelligence
          </Button>
          <Button
            appearance="subtle"
            icon={<DataBarVertical24Regular />}
            onClick={() => navigate("/benchmark/analytics")}
            style={{ color: "#C87800" }}
          >
            Analytics
          </Button>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <Text style={{ fontSize: 12, color: "#555", whiteSpace: "nowrap" }}>Report date:</Text>
            <input type="date" value={reportDate} onChange={e => setReportDate(e.target.value)}
              style={{ padding: "6px 10px", border: "1px solid #D0D7E5", borderRadius: 8, fontSize: 13, outline: "none" }} />
          </div>
          <Button
            appearance="primary"
            icon={exporting ? <Spinner size="tiny" /> : <DocumentArrowDown24Regular />}
            onClick={handleExport}
            disabled={exporting || filtered.length === 0}
            style={{ background: "#A66400", whiteSpace: "nowrap" }}
          >
            Export Excel
          </Button>
          {canSubmit && (
            <Button appearance="secondary" icon={<ArrowUpload24Regular />} onClick={() => navigate("/benchmark/bulk-upload")} style={{ whiteSpace: "nowrap" }}>
              Bulk Upload
            </Button>
          )}
          {canSubmit && (
            <Button appearance="primary" icon={<Add24Regular />} onClick={openForm} style={{ background: "#C87800", whiteSpace: "nowrap" }}>
              Submit Close-Out
            </Button>
          )}
          <Tooltip content="Refresh" relationship="label">
            <Button appearance="subtle" icon={<ArrowSync24Regular />} onClick={refresh} />
          </Tooltip>
        </div>
      </div>

      {/* ── STATS ── */}
      <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
        <StatCard label="Total Records"    value={stats.total}      color="#C87800" />
        <StatCard label="Categories"       value={stats.categories} color="#005A9E" />
        <StatCard
          label="Total Savings"
          value={stats.savings > 0 ? `${records[0]?.currency || "NGN"} ${fmtNum(stats.savings, 0)}` : "—"}
          color="#107C10"
        />
        <StatCard
          label="Avg % Change"
          value={stats.avgPct !== null ? `${stats.avgPct > 0 ? "+" : ""}${stats.avgPct.toFixed(1)}%` : "—"}
          color={stats.avgPct !== null && stats.avgPct < 0 ? "#107C10" : "#C4262E"}
        />
      </div>

      {/* ── FILTERS ── */}
      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E4E8EF", padding: "14px 16px", marginBottom: 16, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ position: "relative", flex: 1, minWidth: 180 }}>
          <Search24Regular style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#999", fontSize: 16 }} />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search tender ref, supplier, description..."
            style={{ width: "100%", boxSizing: "border-box", padding: "8px 10px 8px 34px", border: "1px solid #E4E8EF", borderRadius: 8, fontSize: 13, outline: "none" }}
            onFocus={e  => { e.target.style.borderColor = "#C87800"; }}
            onBlur={e   => { e.target.style.borderColor = "#E4E8EF"; }}
          />
        </div>
        <Field>
          <Select value={filterCat} onChange={(_, d) => setFilterCat(d.value)} style={{ minWidth: 160 }}>
            <option value="All">All Categories</option>
            {BENCHMARK_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </Select>
        </Field>
        <Field>
          <Select value={filterPhase} onChange={(_, d) => setFilterPhase(d.value)} style={{ minWidth: 130 }}>
            <option value="All">All Phases</option>
            <option value="1">Phase 1 — High Value</option>
            <option value="2">Phase 2 — Mid Value</option>
            <option value="3">Phase 3 — Low Value</option>
          </Select>
        </Field>
        <Field>
          <Select value={filterYear} onChange={(_, d) => setFilterYear(d.value)} style={{ minWidth: 100 }}>
            <option value="All">All Years</option>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </Select>
        </Field>
        {(filterCat !== "All" || filterPhase !== "All" || filterYear !== "All" || search) && (
          <Button appearance="subtle" size="small" onClick={() => { setFilterCat("All"); setFilterPhase("All"); setFilterYear("All"); setSearch(""); }} style={{ color: "#888", whiteSpace: "nowrap" }}>
            Clear filters
          </Button>
        )}
      </div>

      {/* ── TABLE ── */}
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>
          <Spinner size="large" label="Loading benchmark records..." />
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E4E8EF", padding: "60px 40px", textAlign: "center" }}>
          <DataBarVertical24Regular style={{ fontSize: 48, color: "#D0D7E5", marginBottom: 12 }} />
          <Text size={500} weight="bold" style={{ color: "#001A3D", display: "block", marginBottom: 8 }}>
            {records.length === 0 ? "No Benchmark Records Yet" : "No Records Match Filters"}
          </Text>
          <Text style={{ color: "#888", display: "block", marginBottom: 20 }}>
            {records.length === 0
              ? "Submit the first tender close-out to begin building the pricing database."
              : "Try clearing the filters or adjusting your search."}
          </Text>
          {canSubmit && records.length === 0 && (
            <Button appearance="primary" icon={<Add24Regular />} onClick={openForm} style={{ background: "#C87800" }}>
              Submit First Close-Out
            </Button>
          )}
        </div>
      ) : (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E4E8EF", overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#001A3D" }}>
                  <th style={{ width: 4, padding: 0 }} />
                  <th style={{ padding: "12px 10px", color: "#C8D8FF", fontSize: 10, fontWeight: 700, letterSpacing: "0.6px", textAlign: "center", width: 38 }}>#</th>
                  <th style={{ padding: "12px 14px", color: "#C8D8FF", fontSize: 10, fontWeight: 700, letterSpacing: "0.6px", textAlign: "left", minWidth: 180 }}>TENDER REFERENCE</th>
                  <th style={{ padding: "12px 14px", color: "#C8D8FF", fontSize: 10, fontWeight: 700, letterSpacing: "0.6px", textAlign: "left", minWidth: 130 }}>CATEGORY</th>
                  <th style={{ padding: "12px 14px", color: "#C8D8FF", fontSize: 10, fontWeight: 700, letterSpacing: "0.6px", textAlign: "left", minWidth: 170 }}>COMMODITY</th>
                  <th style={{ padding: "12px 14px", color: "#C8D8FF", fontSize: 10, fontWeight: 700, letterSpacing: "0.6px", textAlign: "left", minWidth: 150 }}>SUPPLIER</th>
                  <th style={{ padding: "12px 14px", color: "#C8D8FF", fontSize: 10, fontWeight: 700, letterSpacing: "0.6px", textAlign: "right", minWidth: 130 }}>AWARDED RATE</th>
                  <th style={{ padding: "12px 14px", color: "#C8D8FF", fontSize: 10, fontWeight: 700, letterSpacing: "0.6px", textAlign: "center", minWidth: 80 }}>% CHANGE</th>
                  <th style={{ padding: "12px 14px", color: "#C8D8FF", fontSize: 10, fontWeight: 700, letterSpacing: "0.6px", textAlign: "center", minWidth: 90 }}>DATE</th>
                  <th style={{ padding: "12px 14px", color: "#C8D8FF", fontSize: 10, fontWeight: 700, letterSpacing: "0.6px", textAlign: "center", width: 70 }}>PHASE</th>
                  <th style={{ padding: "12px 14px", color: "#C8D8FF", fontSize: 10, fontWeight: 700, letterSpacing: "0.6px", textAlign: "center", width: 60 }}>VIEW</th>
                </tr>
              </thead>
              <tbody>
                {pager.pageItems.map((r, i) => {
                  const idx     = pager.from - 1 + i;
                  const cc2     = catColor(r.category);
                  const ps2     = PHASE_STYLE[r.phase] || PHASE_STYLE[3];
                  const isSelected = selected?.id === r.id;
                  return (
                    <tr
                      key={r.id}
                      onClick={() => setSelected(r)}
                      style={{
                        borderBottom: "1px solid #EEF0F4",
                        background: isSelected ? "#FFF8EE" : "#fff",
                        cursor: "pointer",
                        borderLeft: isSelected ? "3px solid #C87800" : "3px solid transparent",
                        transition: "background 0.12s",
                      }}
                      onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = "#FFFBF2"; }}
                      onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = "#fff"; }}
                    >
                      <td style={{ width: 4, padding: 0 }}><div style={{ width: 4, minHeight: 48, background: cc2 }} /></td>
                      <td style={{ padding: "10px", fontSize: 12, color: "#AAA", textAlign: "center" }}>{idx + 1}</td>
                      <td style={{ padding: "10px 14px", minWidth: 180 }}>
                        <span style={{ fontWeight: 700, color: "#001A3D", fontSize: 12.5, display: "block", lineHeight: 1.4 }}>{r.tenderRef}</span>
                        {r.location && <span style={{ fontSize: 10.5, color: "#888" }}>{r.location}</span>}
                      </td>
                      <td style={{ padding: "10px 14px" }}>
                        <span style={{ background: `${cc2}15`, color: cc2, padding: "2px 8px", borderRadius: 8, fontSize: 10.5, fontWeight: 700 }}>{r.category}</span>
                      </td>
                      <td style={{ padding: "10px 14px", minWidth: 170, maxWidth: 220 }}>
                        {r.commodityGroup ? (
                          <span style={{ fontSize: 12, color: "#333", fontWeight: 600, display: "block", lineHeight: 1.35 }}>
                            {r.commodityGroup}
                          </span>
                        ) : (
                          <Tooltip content="No commodity group recorded — this award cannot be price-compared against other years" relationship="label">
                            <span style={{ fontSize: 11.5, color: "#C4762A", fontStyle: "italic" }}>Not recorded</span>
                          </Tooltip>
                        )}
                        {r.unitOfMeasure && (
                          <span style={{ fontSize: 10.5, color: "#888", display: "block", marginTop: 1 }}>{r.unitOfMeasure}</span>
                        )}
                      </td>
                      <td style={{ padding: "10px 14px" }}>
                        <span style={{ fontSize: 12, color: "#333" }}>{r.supplierName || "—"}</span>
                      </td>
                      <td style={{ padding: "10px 14px", textAlign: "right", fontWeight: 700, color: "#A66400", fontVariantNumeric: "tabular-nums" }}>
                        {r.awardedRate ? `${r.currency} ${fmtNum(r.awardedRate)}` : "—"}
                      </td>
                      <td style={{ padding: "10px 14px", textAlign: "center" }}>
                        <PctBadge value={r.benchmarkRate > 0 ? r.percentageChange : null} />
                      </td>
                      <td style={{ padding: "10px 14px", textAlign: "center", fontSize: 11.5, color: "#555", whiteSpace: "nowrap" }}>
                        {fmtDate(r.tenderDate)}
                      </td>
                      <td style={{ padding: "10px 14px", textAlign: "center" }}>
                        <span style={{ background: ps2.bg, color: ps2.color, padding: "2px 8px", borderRadius: 8, fontSize: 10, fontWeight: 700 }}>{ps2.label}</span>
                      </td>
                      <td style={{ padding: "10px 14px", textAlign: "center" }}>
                        <Tooltip content="View details" relationship="label">
                          <Button
                            appearance="subtle" size="small" icon={<Eye24Regular />}
                            onClick={e => { e.stopPropagation(); setSelected(r); }}
                            style={{ color: "#C87800" }}
                          />
                        </Tooltip>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pagination {...pager} accent="#A66400" noun="record" />
        </div>
      )}

      {/* ── DETAIL PANEL ── */}
      {selected && !formOpen && (
        <DetailPanel
          record={selected}
          onClose={() => setSelected(null)}
          onEdit={openEdit}
          onDelete={handleDelete}
          canEdit={canEdit}
          isAdmin={isAdmin}
        />
      )}

      {/* ── FORM PANEL ── */}
      {formOpen && (
        <CloseOutForm
          initial={editTarget}
          onSave={handleSave}
          onClose={closeForm}
          submitting={submitting}
          records={records}
        />
      )}
    </div>
  );
}
